// sentry-scrub.js — lo que un evento puede llevarse a Sentry, y lo que no.
//
// Sentry es un subencargado en EE.UU.: todo lo que sale por acá es una
// transferencia internacional de lo que contenga. Hasta el 14-sep-2026
// contenía el RUT y el nombre del paciente. El interceptor de axios mandaba
// `extra.url` cruda ante cualquier 5xx, y las URLs del Turno llevan la
// identidad en la query (`paciente-historico/?rut=`, `search-patient/?q=`).
// El `beforeSend` solo depuraba `request.data`, y `request.url` (la página
// donde ocurrió el error) salía entera: `/visualizador-examenes?rut=…`.
// Hallazgo P0-5 de la auditoría Ley 21.719 (Planner #347 / #1235).
//
// Al 14-sep-2026 Sentry está INERTE en producción: no hay DSN ni en el bundle
// ni en el SSM del backend, así que nada de esto llegó a salir (evidencia E6
// del expediente). Esto se arregla igual, y antes de que exista el DSN: el
// orden es DPA con el proveedor, después el scrub, recién después el DSN.
//
// Cuatro reglas, y las cuatro se prueban en sentry-scrub.test.js:
//  1. Ninguna URL sale con la identidad: se corta la query (`?rut=`, `?q=`)
//     y se enmascara el identificador cuando va en el PATH (las tres rutas de
//     `uci_hemo/<rut>/…` lo hacen). Vale en `extra`, en `request.url`, en los
//     headers, en los breadcrumbs y en los spans de tracing.
//  2. Las claves con contenido clínico se tapan en todo lo que el evento
//     trae de la app (`request.data`, `extra`, `contexts`, `tags`,
//     breadcrumbs, spans). Del `exception` se sanea solo el `value` (el
//     mensaje); los `frames` del stack no se tocan.
//  3. Nada de consola en los breadcrumbs: un `console.log(row)` en el Turno
//     es una fila de paciente adentro del evento. Se apaga en la integración
//     (`breadcrumbsIntegration({ console: false })`) y, por si alguien la
//     vuelve a prender, `depurarBreadcrumb` descarta la categoría igual.
//  4. Ningún árbol de DOM sale con el texto de sus atributos. Sentry describe
//     elementos con `htmlTreeAsString`, que incluye siempre `aria-label`,
//     `title`, `alt` y `name`, y el cockpit mobile etiqueta sus botones como
//     `aria-label="Editar <nombre del paciente>"`. Eso aparece en tres
//     lugares distintos: el breadcrumb de clic, los atributos `lcp.element` y
//     `cls.source.N` del span de pageload, y el span STANDALONE de INP.
//
// ⚠️ El span standalone de INP no pasa por `beforeSend` ni por
// `beforeSendTransaction`: se arma con `createSpanEnvelope` y se manda
// directo al transporte. El único gancho que lo ve es `beforeSendSpan`, por
// eso existe `depurarSpan` y por eso `index.js` lo cablea. Devolver `null`
// desde ahí NO descarta el span (el SDK avisa y manda el original igual), así
// que hay que depurarlo, no rechazarlo.

export const TAPADO = "[scrubbed]";

// Claves que se tapan enteras. Van en minúscula; la comparación baja la clave
// del evento antes de mirar. Las primeras dieciocho estaban desde el origen;
// el resto son las columnas del Turno y del LLM que el `PHI_KEYS` original
// no cubría (P0-5: diagnósticos, previo, pendientes, transcripción, text,
// values) más lo que el corpus de ediciones y el visualizador agregan.
export const PHI_KEYS = new Set([
  "patient", "paciente", "rut", "dni", "nombre", "apellido",
  "first_name", "last_name", "fecha_nacimiento", "birth_date",
  "address", "direccion", "phone", "telefono", "email",
  "diagnosis_text", "anamnesis", "history",
  // Turno
  "diagnosticos", "diagnósticos", "dx", "previo", "pendientes",
  "transcripcion", "transcripción", "transcripcion_diaria",
  "evolucion", "evolución", "evoluciones", "indicaciones", "resumen",
  "resumen_paciente", "notas", "notas_turno", "censo", "censo_text",
  "text", "values", "rows", "row", "nombre_paciente", "documento",
  "pasaporte", "edad",
  // LLM y corpus de ediciones
  "prompt", "result_text", "user_output", "model_output", "redaction_map",
  "content", "messages",
  // búsquedas: `?q=<nombre>` cuando viaja como parámetro y no en la URL
  "q", "query", "search", "searchword",
  // atributos que el tracing del SDK pone con la query y el fragmento ya
  // separados de la URL: sin el `?` adelante, la regla de corte no los
  // reconocería, y solos no sirven para diagnosticar nada.
  "http.query", "http.fragment", "url.query", "url.fragment",
]);

// Un token con `?` o `#` seguido de `=` o de `/` es una URL o una query, no
// una pregunta clínica ("fiebre?" y "cama #3" no tienen ninguno de los dos).
const esUrlConParametros = (tok) =>
  (tok.includes("?") || tok.includes("#")) &&
  (tok.includes("=") || tok.includes("/"));

// `turno/paciente-historico/?rut=1-9#x` → `turno/paciente-historico/`.
// Lo que no es cadena vuelve intacto: acá no se inventa nada.
export const sinQuery = (value) => {
  if (typeof value !== "string") return value;
  const corte = Math.min(
    ...["?", "#"].map((c) => value.indexOf(c)).filter((i) => i >= 0),
  );
  return Number.isFinite(corte) ? value.slice(0, corte) : value;
};

// Cortar la query no alcanza: hay rutas que llevan la identidad en el PATH.
// `uci_hemo/<rut>/quick/` (ExamWidgets.js) y `uci_hemo/<rut>/snapshots/`
// (hemoApi.js) mandan el RUT como segmento, así que la URL entera salía por
// el breadcrumb `xhr`, por el span `http.client` y por el `extra.url` del
// interceptor sin que ninguna regla la mirara. Se enmascaran dos cosas: ese
// endpoint por nombre (sirve aunque el identificador sea un pasaporte) y
// cualquier segmento con forma de RUT, para las rutas que vengan después.
const RUT_EN_PATH = /\/\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK](?=\/|$)/g;
const ID_DE_HEMO = /(uci_hemo)\/[^/?#]+/g;
export const sinIdEnPath = (value) =>
  typeof value === "string"
    ? value.replace(ID_DE_HEMO, "$1/[id]").replace(RUT_EN_PATH, "/[id]")
    : value;

// El saneado completo de una URL: sin query, sin fragmento, sin identidad en
// el path. Es lo que se aplica en todos los caminos.
export const sanearUrl = (value) => sinIdEnPath(sinQuery(value));

// Corta la query de cada URL que haya adentro de un texto. Hace falta porque
// Sentry arma cadenas con la URL adentro: el span de tracing se describe
// `GET https://…/paciente-historico/?rut=…` y el breadcrumb de fetch igual.
// Una regla de "cadena sin espacios" dejaba pasar justo esas dos.
export const sinQueryEnTexto = (s) => {
  if (typeof s !== "string") return s;
  return s
    .split(/(\s+)/)
    .map((tok) => (esUrlConParametros(tok) ? sinQuery(tok) : tok))
    .map(sinIdEnPath)
    .join("");
};

// Sentry describe elementos del DOM con `htmlTreeAsString`, que serializa
// siempre `aria-label`, `title`, `alt` y `name` (core/utils/browser.js). El
// cockpit mobile etiqueta el botón como `aria-label="Editar <nombre>"` y el
// visualizador pone `title={patientName}`: eso es el paciente adentro del
// evento. Queda el atributo, se vacía el valor; las clases y el `type`
// siguen, que es lo que sirve para reproducir.
//
// El cierre del atributo se ancla a lo que el SDK escribe después (otro
// atributo, un separador ` > `, un espacio o el fin), porque el valor va sin
// escapar: un nombre con comilla doble rompía un match no anclado.
const ATRIBUTOS_CON_TEXTO =
  /\[(aria-label|title|alt|name|placeholder)="[\s\S]*?"\](?=\[|\s|>|$)/g;
export const sinTextoDeAtributos = (s) =>
  typeof s === "string" ? s.replace(ATRIBUTOS_CON_TEXTO, '[$1="…"]') : s;

// Todo string que sale en un evento pasa por acá: sin identidad en la URL y
// sin el texto de los atributos del DOM.
const sanearTexto = (s) => sinTextoDeAtributos(sinQueryEnTexto(s));

// Camina el valor y devuelve una copia: claves PHI tapadas, strings saneados.
// El tope de profundidad evita quedarse en un objeto cíclico; lo que quede
// más abajo se descarta, que es el lado seguro.
export const depurar = (value, profundidad = 0) => {
  if (profundidad > 10) return TAPADO;
  if (Array.isArray(value)) return value.map((v) => depurar(v, profundidad + 1));
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = PHI_KEYS.has(String(k).toLowerCase())
        ? TAPADO
        : depurar(v, profundidad + 1);
    }
    return out;
  }
  return sanearTexto(value);
};

// Un span, venga de donde venga: del array `spans` de una transacción o del
// envelope standalone de INP, que no pasa por ningún otro gancho.
export const depurarSpan = (span) => {
  if (!span || typeof span !== "object") return span;
  const out = { ...span };
  if (out.description) out.description = sanearTexto(out.description);
  if (out.name) out.name = sanearTexto(out.name);
  if (out.data) out.data = depurar(out.data);
  if (out.attributes) out.attributes = depurar(out.attributes);
  return out;
};

// Un breadcrumb de consola se descarta entero (regla 3). Al resto se le
// depura `data` (url/from/to de navegación y de xhr) y el mensaje, que en los
// de interfaz es el árbol del DOM con sus atributos. Devolver `null` es lo
// que Sentry entiende como "no lo guardes".
export const depurarBreadcrumb = (breadcrumb) => {
  if (!breadcrumb) return breadcrumb;
  if (breadcrumb.category === "console") return null;
  const out = { ...breadcrumb };
  if (out.data) out.data = depurar(out.data);
  out.message = sanearTexto(out.message);
  return out;
};

// Sirve igual para `beforeSend` (errores) y `beforeSendTransaction`
// (tracing): las dos formas de evento llevan request, contexts, extra, tags,
// breadcrumbs y, las transacciones, spans con la URL de cada request.
export const depurarEvento = (event) => {
  if (!event) return event;
  const request = event.request;
  if (request) {
    if (request.url) request.url = sanearUrl(request.url);
    delete request.query_string;
    // `cookies` es la cuarta forma que tiene un evento de llevar la
    // identidad, y la encontró el contraverificador del deploy del 14-sep:
    // el scrub cubría url, query_string, data y headers, y esta pasaba
    // entera. El SDK del browser no la llena hoy, pero el hueco es el mismo
    // de P0-5 y se tapa antes de que exista el DSN, no después.
    if (request.cookies) request.cookies = depurar(request.cookies);
    if (request.data) request.data = depurar(request.data);
    if (request.headers) {
      const headers = { ...request.headers };
      for (const k of Object.keys(headers)) {
        const clave = k.toLowerCase();
        // `Referer` es el caso que importa: el SDK lo llena con
        // `document.referrer`, y con `strict-origin-when-cross-origin` una
        // navegación dentro del mismo origen manda la URL entera. Salir de
        // `/visualizador-examenes?rut=…` por un cierre de sesión dejaba el
        // RUT en el header de todos los eventos de la página siguiente.
        headers[k] =
          clave === "authorization" || clave === "cookie"
            ? TAPADO
            : sanearUrl(headers[k]);
      }
      request.headers = headers;
    }
  }
  if (event.extra) event.extra = depurar(event.extra);
  if (event.tags) event.tags = depurar(event.tags);
  if (event.contexts) {
    delete event.contexts.state;
    // `contexts.trace.data` trae `lcp.element` y `cls.source.N`, que son
    // árboles de DOM con sus atributos: los sanea `depurar`.
    event.contexts = depurar(event.contexts);
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(depurarBreadcrumb).filter(Boolean);
  }
  if (Array.isArray(event.spans)) event.spans = event.spans.map(depurarSpan);
  // Del `exception` se sanea SOLO el mensaje: un `new Error(\`falló ${url}\`)`
  // arrastra la URL. Los `frames` del stack no se tocan, son lo único que
  // permite ubicar el error.
  if (Array.isArray(event.exception?.values)) {
    event.exception.values = event.exception.values.map((v) =>
      v && typeof v === "object" && typeof v.value === "string"
        ? { ...v, value: sanearTexto(v.value) }
        : v,
    );
  }
  if (typeof event.message === "string") event.message = sanearTexto(event.message);
  if (typeof event.transaction === "string") event.transaction = sanearUrl(event.transaction);
  if (event.user) event.user = depurar(event.user);
  return event;
};
